import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Sparkle, Question, MagnifyingGlass, ChatCircleDots, Flag, Check, X } from "@phosphor-icons/react";

const Tool = ({ title, icon, desc, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2 mb-1">{icon}<h3 className="font-display font-bold text-slate-900">{title}</h3></div>
    <p className="text-sm text-slate-500 mb-3">{desc}</p>
    {children}
  </div>
);

// Accept-into-draft / dismiss-with-reason control, recorded to the audit trail.
function Decision({ tool, rid, suggestion, onAccept, acceptLabel = "Accept into draft" }) {
  const [reason, setReason] = useState("");
  const [asking, setAsking] = useState(false);
  const [done, setDone] = useState("");
  const record = async (decision) => {
    try {
      await api.post("/policies/ai/decision", { tool, rid: rid || null, decision, suggestion, reason });
      setDone(decision); toast.success(decision === "accept" ? "Accepted (recorded)" : "Dismissed (recorded)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record decision"); }
  };
  if (done) return <div className="text-xs font-semibold text-slate-500 mt-2" data-testid="decision-done">Recorded: {done}</div>;
  return (
    <div className="mt-2 flex flex-col gap-2">
      <div className="flex gap-2">
        <Button size="sm" data-testid="decision-accept" onClick={async () => { if (onAccept) await onAccept(); record("accept"); }} className="bg-emerald-700 hover:bg-emerald-800 gap-1"><Check size={14} /> {acceptLabel}</Button>
        <Button size="sm" variant="outline" data-testid="decision-dismiss-open" onClick={() => setAsking(!asking)} className="gap-1 text-red-600 border-red-200 hover:bg-red-50"><X size={14} /> Dismiss</Button>
      </div>
      {asking && (
        <div className="flex gap-2">
          <Input data-testid="decision-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for dismissing (required)" className="h-9" />
          <Button size="sm" data-testid="decision-dismiss-confirm" disabled={!reason.trim()} onClick={() => record("dismiss")} variant="outline">Confirm</Button>
        </div>
      )}
    </div>
  );
}

export default function PolicyAI() {
  const [busy, setBusy] = useState("");
  const [docs, setDocs] = useState([]);
  // draft
  const [dTitle, setDTitle] = useState("");
  const [dPrompt, setDPrompt] = useState("");
  const [draft, setDraft] = useState("");
  const [questions, setQuestions] = useState("");
  // review
  const [reviewRid, setReviewRid] = useState("");
  const [reviewIn, setReviewIn] = useState("");
  const [reviewOut, setReviewOut] = useState("");
  // triage
  const [triageIn, setTriageIn] = useState("");
  const [triageOut, setTriageOut] = useState("");
  // flag for review
  const [flagRid, setFlagRid] = useState("");
  const [flagTrigger, setFlagTrigger] = useState("");
  const [flagOut, setFlagOut] = useState("");

  useEffect(() => { api.get("/policies/authoring/drafts").then((r) => setDocs(r.data)).catch(() => {}); }, []);
  const published = docs.filter((d) => d.published);

  const call = async (key, url, payload, setter, pick) => {
    setBusy(key);
    try { const r = await api.post(url, payload); setter(pick ? r.data[pick] : r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI request failed"); }
    finally { setBusy(""); }
  };

  const sendDraftToEditor = async () => {
    const r = await api.post("/policies/authoring", { title: dTitle || "AI draft", body: { html: draft } });
    toast.success("Created as an unassigned draft — open it in Authoring to edit");
    return r.data.document.id;
  };

  return (
    <div className="flex flex-col gap-4" data-testid="policy-ai">
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
        AI is restricted to authorised admins & policy owners. It proposes editable drafts only — it never publishes, and staff have no AI access. Company-specific facts it doesn't know are marked <code>[[NEEDS INPUT]]</code>. A person always makes the final decision.
      </div>

      <Tool title="Create a policy with AI" icon={<Sparkle size={18} className="text-emerald-700" />} desc="Describe your requirement; AI prepares an editable draft you can send straight to the editor.">
        <Input data-testid="ai-draft-title" value={dTitle} onChange={(e) => setDTitle(e.target.value)} placeholder="Policy title (e.g. Lone Working)" className="mb-2" />
        <Textarea data-testid="ai-draft-prompt" value={dPrompt} onChange={(e) => setDPrompt(e.target.value)} placeholder="Context: your trade, sites, who's affected, any UK rules to reflect…" className="min-h-[80px] mb-2" />
        <Button data-testid="ai-draft-btn" disabled={busy === "draft"} onClick={() => call("draft", "/policies/ai/draft", { title: dTitle, prompt: dPrompt }, setDraft, "html")} className="bg-emerald-700 hover:bg-emerald-800">{busy === "draft" ? "Drafting…" : "Generate draft"}</Button>
        {draft && <div className="mt-3"><div className="prose-tmic policy-html border border-slate-200 rounded-lg p-3 max-h-64 overflow-y-auto" data-testid="ai-draft-out" dangerouslySetInnerHTML={{ __html: draft }} />
          <Decision tool="draft" suggestion={draft} acceptLabel="Create draft in editor" onAccept={sendDraftToEditor} /></div>}
      </Tool>

      <Tool title="Clarification questions" icon={<Question size={18} className="text-blue-600" />} desc="AI asks targeted questions about your actual practices before drafting.">
        <Input data-testid="ai-q-title" value={dTitle} onChange={(e) => setDTitle(e.target.value)} placeholder="Policy topic" className="mb-2" />
        <Button data-testid="ai-q-btn" disabled={busy === "q"} onClick={() => call("q", "/policies/ai/questions", { title: dTitle, prompt: dPrompt }, setQuestions, "questions")} variant="outline">{busy === "q" ? "Thinking…" : "Get questions"}</Button>
        {questions && <pre data-testid="ai-q-out" className="mt-3 whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{questions}</pre>}
      </Tool>

      <Tool title="Contradiction & gap review" icon={<MagnifyingGlass size={18} className="text-amber-600" />} desc="Pick an existing published policy (AI cites its ref/revision) or paste text; AI flags contradictions, gaps vs UK duties and unclear wording.">
        <select data-testid="ai-review-doc" value={reviewRid} onChange={(e) => { setReviewRid(e.target.value); setReviewIn(""); }} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm mb-2">
          <option value="">— Paste text below —</option>
          {published.map((d) => <option key={d.id} value={d.id}>{d.title} ({d.published.revision})</option>)}
        </select>
        {!reviewRid && <Textarea data-testid="ai-review-in" value={reviewIn} onChange={(e) => setReviewIn(e.target.value)} placeholder="Paste policy content…" className="min-h-[80px] mb-2" />}
        <Button data-testid="ai-review-btn" disabled={busy === "rev"} onClick={() => call("rev", "/policies/ai/review", reviewRid ? { rid: reviewRid } : { html: reviewIn }, setReviewOut, "review")} variant="outline">{busy === "rev" ? "Reviewing…" : "Review"}</Button>
        {reviewOut && <><pre data-testid="ai-review-out" className="mt-3 whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{reviewOut}</pre>
          <Decision tool="review" rid={reviewRid} suggestion={reviewOut} acceptLabel="Accept findings" /></>}
      </Tool>

      <Tool title="Flag a policy for review" icon={<Flag size={18} className="text-red-600" />} desc="After a workflow or legal change, flag a policy so the owner reviews it. AI can explain why. Records an audit event and an in-app notice — it never changes content.">
        <select data-testid="ai-flag-doc" value={flagRid} onChange={(e) => setFlagRid(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm mb-2">
          <option value="">— Choose a policy —</option>
          {docs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>
        <Textarea data-testid="ai-flag-trigger" value={flagTrigger} onChange={(e) => setFlagTrigger(e.target.value)} placeholder="What changed? (e.g. we moved to a new site with a one-way traffic system)" className="min-h-[70px] mb-2" />
        <Button data-testid="ai-flag-btn" disabled={busy === "flag" || !flagRid} onClick={() => call("flag", "/policies/ai/flag-review", { rid: flagRid, trigger: flagTrigger, ai_assist: true }, (d) => setFlagOut(d.reason), null)} className="bg-red-600 hover:bg-red-700 gap-1"><Flag size={14} /> {busy === "flag" ? "Flagging…" : "Flag for review"}</Button>
        {flagOut && <div data-testid="ai-flag-out" className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">{flagOut}</div>}
      </Tool>

      <Tool title="Feedback triage" icon={<ChatCircleDots size={18} className="text-slate-600" />} desc="Paste staff feedback; AI classifies it and drafts a reply for you to approve. You always send it yourself.">
        <Textarea data-testid="ai-triage-in" value={triageIn} onChange={(e) => setTriageIn(e.target.value)} placeholder="Paste staff feedback…" className="min-h-[70px] mb-2" />
        <Button data-testid="ai-triage-btn" disabled={busy === "tri"} onClick={() => call("tri", "/policies/ai/triage", { text: triageIn }, setTriageOut, "triage")} variant="outline">{busy === "tri" ? "Triaging…" : "Triage"}</Button>
        {triageOut && <><pre data-testid="ai-triage-out" className="mt-3 whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{triageOut}</pre>
          <Decision tool="triage" suggestion={triageOut} acceptLabel="Accept draft reply" /></>}
      </Tool>
    </div>
  );
}
